import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { accountApi } from "../lib/api/account";
import { authApi } from "../lib/api/auth";
import { isApiError } from "../lib/api/http";
import type { Me } from "../lib/api/types";

export type SessionState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "error"; error: unknown }
  | { status: "signed-in"; me: Me };

interface SessionValue {
  state: SessionState;
  /** Re-reads /v1/me, e.g. after signing in or accepting an invite. */
  refresh: () => Promise<Me | null>;
  setMe: (me: Me) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  const refresh = useCallback(async () => {
    try {
      const me = await accountApi.me();
      setState({ status: "signed-in", me });
      return me;
    } catch (e) {
      setState(isApiError(e, "UNAUTHENTICATED") ? { status: "signed-out" } : { status: "error", error: e });
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    await authApi.signOut().catch(() => undefined);
    setState({ status: "signed-out" });
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({ state, refresh, signOut, setMe: (me) => setState({ status: "signed-in", me }) }),
    [state, refresh, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

/** For screens behind RequireSession, where a signed-in user is guaranteed. */
export function useMe(): Me {
  const { state } = useSession();
  if (state.status !== "signed-in") throw new Error("useMe needs a signed-in session");
  return state.me;
}
